// components/layout/NavMenu/hooks/useNavPermissions.ts
import { useMemo } from 'react';
import type { UserData } from '../../../../context/AuthContext';

export interface NavPermissionsInput {
  user: UserData | null;
  isActingLeadFromTeams: boolean;
  /** Tournament-specific role override (from staffAssignments) */
  tournamentRole?: string | null;
  /** Store-manager (proxy) delegated permissions. Aggregated
   *  flags from useStoreManagerships — when any active membership
   *  grants the permission, the corresponding flag here is true.
   *  Defaults to all-false when not supplied so callers that
   *  don't know about store delegation still work unchanged. */
  storeMgrCanCreateTournaments?: boolean;
  storeMgrCanManageSeries?: boolean;
}

export interface NavPermissions {
  /** The effective role being used (tournament role if available, else default) */
  role: string;
  /** The user's default role (from user.role) */
  defaultRole: string;
  /** The tournament-specific role (if any) */
  tournamentRole: string | null;
  isAdmin: boolean;
  isStoreOwner: boolean;
  isPermanentManager: boolean;
  isManager: boolean;
  isHeadJudge: boolean;
  isScorekeeper: boolean;
  isFeatureMatch: boolean;
  isTeamLead: boolean;
  isActingLead: boolean;
  isOnAnyFloorTeam: boolean;
  hasFeatureMatchRole: boolean;
  isInFeatureMatchTeam: boolean;
  hasFeatureMatchAccess: boolean;
  hasHeadJudgeRole: boolean;
  isInHeadJudgeTeam: boolean;
  hasHeadJudgeAccess: boolean;
  hasDeckCheckRole: boolean;
  isInDeckCheckTeam: boolean;
  canViewDeckCheck: boolean;
  canViewAppeals: boolean;
  canManage: boolean;
  canSetupTournament: boolean;
  canApprovePenalties: boolean;
  canViewTimer: boolean;
  canViewTournamentEngine: boolean;
  canViewStaffSettings: boolean;
  canViewProblemTracker: boolean;
  canViewJobBoard: boolean;
  canManageJobApplications: boolean;
  canViewStream: boolean;
  canViewMatchmaking: boolean;
}

/**
 * Hook for computing navigation permissions based on user role and team assignments
 * Uses accountType (new system) with fallback to role (legacy) for backwards compatibility
 *
 * When a tournamentRole is provided, it overrides the default role for role-based checks.
 * This allows the nav menu to show tournament-specific features based on the user's
 * role in the currently selected tournament.
 */
export function useNavPermissions({
  user,
  isActingLeadFromTeams,
  tournamentRole: tournamentRoleProp,
  storeMgrCanCreateTournaments = false,
  storeMgrCanManageSeries = false,
}: NavPermissionsInput): NavPermissions {
  const accountType = user?.accountType;
  const defaultRole = user?.role || '';

  // Use tournament role if provided and not empty, otherwise fall back to default
  const effectiveRole = (tournamentRoleProp && tournamentRoleProp.trim()) ? tournamentRoleProp : defaultRole;

  return useMemo(() => {
    // Base role checks: accountType first (new), then effectiveRole (tournament-specific or default)
    // Note: isAdmin and isStoreOwner are global account types, not tournament-specific
    const isAdmin = accountType === 'admin' || defaultRole === 'admin';
    const isStoreOwner = accountType === 'store';

    // Role-based checks use effectiveRole (tournament role if available)
    const isManager = effectiveRole === 'Manager';
    const isHeadJudge = effectiveRole === 'Head Judge';
    const isScorekeeper = effectiveRole === 'Scorekeeper';
    const isFeatureMatch = effectiveRole === 'Feature Match';
    const isTeamLead = effectiveRole === 'Team Lead';

    // Permanent manager check (staff with permanentStoreId and Manager role)
    const permanentStoreId = (user as { permanentStoreId?: string } | null)?.permanentStoreId;
    const permanentStoreRole = (user as { permanentStoreRole?: string } | null)?.permanentStoreRole;
    const isPermanentManager = Boolean(permanentStoreId && permanentStoreRole === 'Manager');

    // Acting lead check from user object OR computed from team data
    const hasActingLeadField = Boolean(user?.actingTeamLeadFor && Object.keys(user.actingTeamLeadFor).length > 0);
    const isActingLead = hasActingLeadField || isActingLeadFromTeams;

    // Floor team assignment check - true if user is assigned to ANY floor team
    const isOnAnyFloorTeam = Boolean(user?.floorTeamAssignments && Object.keys(user.floorTeamAssignments).length > 0);

    // Feature Match access (store owners get manager-level access)
    const hasFeatureMatchRole = isAdmin || isStoreOwner || isManager || isHeadJudge || isFeatureMatch;
    const isInFeatureMatchTeam = Boolean(user?.featureMatchTeamAssignments && Object.keys(user.featureMatchTeamAssignments).length > 0);
    const hasFeatureMatchAccess = hasFeatureMatchRole || isInFeatureMatchTeam;

    // Head Judge access (store owners get manager-level access)
    const hasHeadJudgeRole = isAdmin || isStoreOwner || isManager || isHeadJudge;
    const isInHeadJudgeTeam = Boolean(user?.headJudgeTeamAssignments && Object.keys(user.headJudgeTeamAssignments).length > 0);
    const hasHeadJudgeAccess = hasHeadJudgeRole || isInHeadJudgeTeam;

    // Deck check access (HJ team has oversight over deck checks)
    const hasDeckCheckRole = hasFeatureMatchAccess;
    const isInDeckCheckTeam = Boolean(user?.deckCheckTeamAssignments && Object.keys(user.deckCheckTeamAssignments).length > 0);
    const canViewDeckCheck = hasDeckCheckRole || isInDeckCheckTeam || isInHeadJudgeTeam;

    // Permission combinations (store owners have manager-level
    // access within their store). Store-manager proxies get the
    // same level of access scoped to whatever their delegation
    // grants — canSetupTournament + canManage trigger off
    // storeMgrCanCreateTournaments / Series, the navigation
    // engine + matchmaking items trigger off canCreateTournaments
    // (since reaching the runner is the natural follow-up to
    // creating an event).
    const canViewAppeals = hasHeadJudgeAccess;
    const canManage =
      isAdmin || isStoreOwner || isManager ||
      storeMgrCanCreateTournaments || storeMgrCanManageSeries;
    const canSetupTournament =
      isAdmin || isStoreOwner || isManager || isScorekeeper ||
      storeMgrCanCreateTournaments;
    const canApprovePenalties = hasHeadJudgeAccess || isTeamLead || isActingLead;
    const canViewTimer = isStoreOwner || ['Head Judge', 'Scorekeeper', 'Manager', 'admin', 'Team Lead', 'Floor Judge', 'Feature Match'].includes(effectiveRole) || isInHeadJudgeTeam || isInFeatureMatchTeam || isOnAnyFloorTeam;
    const canViewTournamentEngine =
      isAdmin || isStoreOwner || isManager || isHeadJudge ||
      isScorekeeper || isOnAnyFloorTeam ||
      storeMgrCanCreateTournaments;
    // Matchmaking nav: same visibility as the tournament engine. Scorekeepers
    // see it so they can reach the Players tab for on-the-spot registration.
    const canViewMatchmaking =
      isAdmin || isStoreOwner || isManager || isHeadJudge ||
      isScorekeeper || storeMgrCanCreateTournaments;
    const canViewStaffSettings = isStoreOwner || ['admin', 'Manager', 'Head Judge', 'Scorekeeper', 'Team Lead', 'Floor Judge', 'Feature Match', 'Shield', 'Deck Check'].includes(effectiveRole) || isOnAnyFloorTeam;
    const canViewProblemTracker = isAdmin || isStoreOwner || isManager || isScorekeeper || isHeadJudge || isOnAnyFloorTeam;

    // Job board permissions
    const canViewJobBoard = isAdmin || isStoreOwner || isPermanentManager || accountType === 'staff';
    const canManageJobApplications = isAdmin || isStoreOwner || isPermanentManager;

    // Stream permissions
    const isStream = effectiveRole === 'Stream' || defaultRole === 'Stream';
    const canViewStream = isAdmin || isStoreOwner || isManager || isStream;

    // Determine display role: use accountType-based label when no legacy/tournament role
    let displayRole = effectiveRole;
    if (!displayRole || displayRole === 'pending') {
      if (isAdmin) displayRole = 'admin';
      else if (isStoreOwner) displayRole = 'Store';
      else if (accountType === 'staff') displayRole = 'Staff';
      else if (accountType) displayRole = accountType;
    }

    return {
      role: displayRole,
      defaultRole,
      tournamentRole: tournamentRoleProp || null,
      isAdmin,
      isStoreOwner,
      isPermanentManager,
      isManager,
      isHeadJudge,
      isScorekeeper,
      isFeatureMatch,
      isTeamLead,
      isActingLead,
      isOnAnyFloorTeam,
      hasFeatureMatchRole,
      isInFeatureMatchTeam,
      hasFeatureMatchAccess,
      hasHeadJudgeRole,
      isInHeadJudgeTeam,
      hasHeadJudgeAccess,
      hasDeckCheckRole,
      isInDeckCheckTeam,
      canViewDeckCheck,
      canViewAppeals,
      canManage,
      canSetupTournament,
      canApprovePenalties,
      canViewTimer,
      canViewTournamentEngine,
      canViewStaffSettings,
      canViewProblemTracker,
      canViewJobBoard,
      canManageJobApplications,
      canViewStream,
      canViewMatchmaking,
    };
  }, [
    accountType,
    defaultRole,
    effectiveRole,
    tournamentRoleProp,
    user,
    isActingLeadFromTeams,
    // Including the proxy delegation flags so the memo
    // recomputes when the store toggles a permission. Without
    // these, canSetupTournament / canManage / etc. stayed at
    // their initial values even after the storeManagership
    // subscription fired with new data — which was the actual
    // cause of 'proxy can still see Job Board + Dashboard but
    // Tournament Setup remains hidden' even when the store had
    // already granted permission.
    storeMgrCanCreateTournaments,
    storeMgrCanManageSeries,
  ]);
}

export default useNavPermissions;
